import {reset, startSubmit, stopSubmit} from "redux-form";
import {eventChannel} from "redux-saga";
import {all, call, cancel, cancelled, fork, put, select, take,} from "redux-saga/effects";
import {Company} from "../../domain/model/company";
import {Customer} from "../../domain/model/customer";
import {Message} from "../../domain/model/message";
import {MessageRoom} from "../../domain/model/message_room";
import {Project} from "../../domain/model/project";
import {User} from "../../domain/model/user";
import {ICompanyRepository} from "../../domain/repository/company_repository";
import {ICustomerRepository} from "../../domain/repository/customer_repository";
import {IMessageRoomRepository} from "../../domain/repository/message_room_repository";
import {IProjectRepository} from "../../domain/repository/project_repository";
import {createChatUsecase, IChatUseCase,} from "../../domain/usecase/chat_usecase";
import {createFileUsecase, IFileUseCase,} from "../../domain/usecase/file_usecase";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {createCompanyAPI} from "../../infra/api/service/company_api";
import {createCustomerAPI} from "../../infra/api/service/customer_api";
import {createMessageRoomAPI} from "../../infra/api/service/message_room_api";
import {createProjectAPI} from "../../infra/api/service/project_api";
import {
    ClientMessageRoomActionType,
    createClientMessageRoomActionCreator,
    IClientMessageRoomActionCreator,
    IRequestCreateMessageRoomAction,
    IRequestSendMessageRoomAction,
} from "../action/client_message_room_action";
import {
    CreateMessageRoomByClientResponse,
    ObserveMessageRoomByClientResponse,
} from "../response/message_room_response";
import {AppState} from "../store/app_state";
import {AuthState} from "../store/auth_state";
import {TMessageSignature} from "../type/common";
import {handleErrorForHandler} from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const messageRoomRepository: IMessageRoomRepository =
    createMessageRoomAPI(apiClient);
const chatUseCase: IChatUseCase = createChatUsecase();
const fileUseCase: IFileUseCase = createFileUsecase();
const actionCreator: IClientMessageRoomActionCreator =
    createClientMessageRoomActionCreator();

let task: any;

function* handleCreateMessageRoomInClientMessageRoom() {
    while (true) {
        try {
            const action: IRequestCreateMessageRoomAction = yield take(
                ClientMessageRoomActionType.REQUEST_CREATE_MESSAGE_ROOM
            );
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            const companyId =
                _authState.user && _authState.user.client
                    ? _authState.user.client.companyID
                    : "";
            let company: Company;
            let messagesOrigin: Message[];
            let messageRoom: MessageRoom;
            let project: Project;
            let customers: Customer[];
            [company, messagesOrigin, messageRoom, project, customers] = yield all([
                call(getCompany, companyId),
                call(
                    createMessageRoom,
                    user,
                    action.item.projectId,
                    action.item.opponentId
                ),
                call(getMessageRoom, action.item.projectId, action.item.opponentId),
                call(getProject, action.item.projectId),
                call(getCustomers, action.item.opponentId),
                call(read, action.item.projectId, action.item.opponentId),
            ]);

            const ownSignature = {
                id: companyId,
                name: company.name,
                iconURL: company.getLogoURL(),
                description: company.introduction,
            };
            const messages = messagesOrigin.concat();
            const customer: Customer = customers[0];
            const opponentSignature: TMessageSignature = {
                id: customer.id,
                name: customer.getCatchphrase(),
                iconURL: customer.getIconURL(),
                description: customer.pr,
            };

            const res: CreateMessageRoomByClientResponse =
                new CreateMessageRoomByClientResponse();
            res.messageRoom = messageRoom;
            res.messages = messages;
            res.project = project;
            res.ownSignature = ownSignature;
            res.opponentSignature = opponentSignature;
            task = yield fork(watchMessages);
            yield put(actionCreator.callbackCreateMessageRoomAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackCreateMessageRoomAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

function* handleSendMessageRoomInClientMessageRoom() {
    while (true) {
        try {
            const action: IRequestSendMessageRoomAction = yield take(
                ClientMessageRoomActionType.REQUEST_SEND_MESSAGE_ROOM
            );
            yield put(startSubmit("clientMessageRoomForm"));
            const imageUrl: string = "";
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            const fileUrl: string = action.item.uploadFile
                ? yield call(
                    createMessageFile,
                    user,
                    action.item.uploadFile,
                    action.item.projectId,
                    action.item.opponentId
                )
                : "";
            yield call(
                sendMessageRoom,
                user,
                action.item.text,
                imageUrl,
                fileUrl,
                action.item.opponentId
            );
            yield put(actionCreator.callbackSendMessageRoomAction(true));
            yield put(stopSubmit("clientMessageRoomForm"));
            yield put(reset("clientMessageRoomForm"));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                stopSubmit("clientMessageRoomForm", {_error: error.toString()})
            );
        }
    }
}

function* handleDestroyMessageRoomInClientMessageRoom() {
    while (true) {
        try {
            yield take(ClientMessageRoomActionType.REQUEST_DESTROY_MESSAGE_ROOM);
            yield cancel(task);
            yield put(actionCreator.callbackDestroyMessageRoomAction(true));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackDestroyMessageRoomAction(false, error.toString())
            );
        }
    }
}

function* messageChannel() {
    return eventChannel((emit) => {
        chatUseCase.observeMessage((message) => {
            emit(message);
        });

        const unsubscribe = () => {
            chatUseCase.closeRoom();
        };

        return unsubscribe;
    });
}

function* watchMessages() {
    const messages = yield call(messageChannel);
    try {
        while (true) {
            const message: Message = yield take(messages);
            const res: ObserveMessageRoomByClientResponse =
                new ObserveMessageRoomByClientResponse();
            res.message = message;
            yield put(actionCreator.callbackObserveMessageRoomAction(true, res));
        }
    } catch (error) {

    } finally {
        if (yield cancelled()) {
            messages.close();
        }
    }
}

const getMessageRoom = (
    projectID: string,
    opponentID: string
): Promise<MessageRoom> => {
    return messageRoomRepository.get(projectID, opponentID);
};

const getCustomers = (customerID: string): Promise<Customer[]> => {
    return customerRepository.getAllByIDs([customerID]);
};

const getProject = (projectId: string): Promise<Project> => {
    return projectRepository.get(projectId);
};

const getCompany = (companyId: string): Promise<Company> => {
    return companyRepository.get(companyId);
};

const createMessageRoom = (
    user: User,
    projectId: string,
    opponentId: string
) => {
    return chatUseCase.connectRoom(user, projectId, opponentId);
};

const sendMessageRoom = (
    user: User,
    text: string,
    imageUrl: string,
    fileUrl: string,
    toId: string
) => {
    return chatUseCase.sendMessage(user, text, imageUrl, fileUrl, toId);
};

const read = (projectId: string, opponentId: string): Promise<void> => {
    return messageRoomRepository.read(projectId, opponentId);
};

const createMessageFile = (
    user: User,
    uploadFile: File,
    projectId: string,
    opponentId: string
): Promise<string> => {
    return fileUseCase.createMessageFile(user, uploadFile, projectId, opponentId);
};

export {
    handleCreateMessageRoomInClientMessageRoom,
    handleSendMessageRoomInClientMessageRoom,
    handleDestroyMessageRoomInClientMessageRoom,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/client_message_room_handler.ts